/* eslint-disable no-restricted-syntax */
import type { ControllerRenderProps, FieldValues } from "react-hook-form"
import type * as z from "zod"
import type { INPUT_COMPONENTS } from "./config"
import type { TSelectorItem } from "@/models/common.model"

// Tipo extendido para opciones con soporte para headers y separadores
export type ExtendedSelectorItem = TSelectorItem & {
  isHeader?: boolean
}

export type FormatConfig = {
  /** Función que transforma el valor ingresado */
  transform?: (value: string) => string
  /** Función que valida el valor final */
  validate?: (value: string) => boolean
  /** Función que formatea el valor para mostrar */
  format?: (value: string) => string
  /** Longitud máxima del valor */
  maxLength?: number
  /** Longitud mínima para ser válido */
  minLength?: number
  /** Placeholder del input */
  placeholder?: string
  /** Mensaje de error de validación */
  errorMessage?: string
  /** Relleno automático (ej: guiones al final) */
  fillCharacter?: string
  /** Longitud total deseada para el relleno */
  targetLength?: number
  /** Clases CSS adicionales */
  inputClassName?: string
}

export type FieldConfigItem<InnerType = unknown> = {
  description?: React.ReactNode
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> &
    React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
      showLabel?: boolean
      options?: ExtendedSelectorItem[]
      formatconfig?: FormatConfig
      disableAllDatesWithoutRange?: boolean
      maxTotalMedals?: number
      showSelectAll?: boolean
    }
  label?: string
  fieldType?:
    | keyof typeof INPUT_COMPONENTS
    | React.FC<AutoFormInputComponentProps>
  renderParent?: (props: {
    children: React.ReactNode
  }) => React.ReactElement | null
  order?: number
  containerClassName?: string
  // For arrays of objects, allow nested config
  // @ts-expect-error - InnerType is not a valid type
  fields?: FieldConfig<InnerType>
}

export type FieldConfig<SchemaType extends z.infer<z.ZodObject<any, any>>> = {
  [Key in keyof SchemaType]?: SchemaType[Key] extends Array<infer ArrayType>
    ? ArrayType extends object
      ? FieldConfigItem<ArrayType>
      : FieldConfigItem
    : SchemaType[Key] extends z.ZodDate
      ? FieldConfigItem
      : SchemaType[Key] extends object
        ? FieldConfig<z.infer<SchemaType[Key]>>
        : FieldConfigItem
}

export enum DependencyType {
  DISABLES,
  REQUIRES,
  HIDES,
  SETS_OPTIONS,
}

type BaseDependency<SchemaType> = {
  sourceField: keyof SchemaType
  type: DependencyType
  targetField: keyof SchemaType
  when: (sourceFieldValue: unknown, targetFieldValue: unknown) => boolean
}

export type ValueDependency<SchemaType> =
  BaseDependency<SchemaType> & {
    type:
      | DependencyType.DISABLES
      | DependencyType.REQUIRES
      | DependencyType.HIDES
  }

export type EnumValues = readonly [string, ...string[]]

export type OptionsDependency<
  SchemaType,
> = BaseDependency<SchemaType> & {
  type: DependencyType.SETS_OPTIONS
  options: EnumValues
}

export type Dependency<SchemaType> =
  | ValueDependency<SchemaType>
  | OptionsDependency<SchemaType>

/**
 * A FormInput component can handle a specific Zod type (e.g. "ZodBoolean")
 */
export type AutoFormInputComponentProps = {
  zodInputProps: React.InputHTMLAttributes<HTMLInputElement>
  // @ts-expect-error - Field  is too broad
  field: ControllerRenderProps<FieldValues, unknown>
  fieldConfigItem: FieldConfigItem
  label: string
  isRequired: boolean
  fieldProps: Record<string, unknown> & {
    disableAllDatesWithoutRange?: boolean
    className?: string
  }
  zodItem: z.ZodAny
  className?: string
}
